// Consulta dos cards de uma fila com card próprio (ex.: Contratação → candidatos):
// colunas por etapa, busca (dados e respostas dos formulários) e o card completo ao clicar.
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button, Card, CardActionArea, CardContent, Checkbox, Chip, CircularProgress, Drawer, FormControlLabel, IconButton,
  InputAdornment, Paper, TextField, Typography, makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import suporteApi, { mensagemErro } from "../api";
import { haQuanto } from "../comum";
import CardFila from "../CardFila";

const useStyles = makeStyles((theme) => ({
  quadro: { display: "flex", gap: 12, overflowX: "auto", flex: 1, paddingBottom: 8, ...theme.scrollbarStyles },
  coluna: { minWidth: 270, maxWidth: 270, display: "flex", flexDirection: "column", background: theme.palette.action.hover, borderRadius: 8 },
  cabecalho: { padding: "8px 10px", fontWeight: 600, fontSize: 12.5, display: "flex", justifyContent: "space-between", gap: 6 },
  lista: { padding: "0 8px 8px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, ...theme.scrollbarStyles },
  card: { borderLeft: `4px solid ${theme.palette.primary.main}` },
  fechado: { borderLeftColor: theme.palette.text.disabled, opacity: 0.8 },
  validar: { borderLeftColor: "#7b1fa2", background: theme.palette.type === "dark" ? "rgba(123,31,162,.22)" : "rgba(123,31,162,.08)" },
  fraco: { color: theme.palette.text.secondary, fontSize: 12 },
  ficha: { width: 420, maxWidth: "100vw", padding: 12 },
  filtros: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", marginBottom: 8 },
}));

const CardsDaFila = () => {
  const c = useStyles();
  const history = useHistory();
  const [filas, setFilas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [fila, setFila] = useState(null);
  const [dados, setDados] = useState(null);
  const [busca, setBusca] = useState("");
  const [termo, setTermo] = useState("");
  const [concluidos, setConcluidos] = useState(false);
  const [aberto, setAberto] = useState(null);

  useEffect(() => {
    Promise.all([suporteApi.get("/api/formularios/filas"), suporteApi.get("/api/modelos-card")])
      .then(([f, m]) => {
        const comCard = m.data.filter((x) => x.fila_id > 0);
        setModelos(comCard);
        const visiveis = f.data.filter((q) => comCard.some((x) => x.fila_id === q.id));
        setFilas(visiveis);
        setFila((atual) => atual ?? visiveis[0]?.id ?? null);
      })
      .catch((err) => toast.error(mensagemErro(err)));
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setTermo(busca), 350);
    return () => clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    if (!fila) return;
    try {
      const { data } = await suporteApi.get(`/api/cards-fila/${fila}`, { params: { busca: termo, concluidos } });
      setDados(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  }, [fila, termo, concluidos]);
  useEffect(() => {
    carregar();
    const t = setInterval(() => !document.hidden && carregar(), 20000);
    return () => clearInterval(t);
  }, [carregar]);

  const abrir = async (id) => {
    try {
      setAberto((await suporteApi.get(`/api/atendimentos/${id}`)).data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  const modelo = dados?.modelo || modelos.find((x) => x.fila_id === fila);
  const etapas = modelo?.etapas || [];
  const colunas = [...etapas, ...((dados?.cards || []).some((x) => !etapas.includes(x.etapa)) ? ["(sem etapa)"] : [])];
  const daColuna = (et) => (dados?.cards || []).filter((x) => (etapas.includes(x.etapa) ? x.etapa : "(sem etapa)") === et);
  const resumo = (x) => {
    const campos = x.campos || {};
    const nome = campos.nome || x.solicitante || x.whatsapp_numero || `#${x.id}`;
    const extras = (modelo?.campos || []).filter((cp) => cp.id !== "nome" && cp.id !== "telefone" && campos[cp.id])
      .slice(0, 3).map((cp) => campos[cp.id]);
    return { nome, extras };
  };

  if (!filas.length) {
    return (
      <MainContainer>
        <MainHeader><Title>Cards das filas</Title></MainHeader>
        <Typography color="textSecondary" style={{ padding: 16 }}>
          Nenhuma fila sua tem card próprio. Configure em Administração → Cards por fila.
        </Typography>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{`${modelo?.nome || "Cards"} · ${filas.find((q) => q.id === fila)?.nome || ""} (${dados?.cards.length ?? 0})`}</Title>
        <MainHeaderButtonsWrapper>
          {filas.length > 1 && filas.map((q) => (
            <Chip key={q.id} clickable color={q.id === fila ? "primary" : "default"} label={q.nome} onClick={() => setFila(q.id)} />
          ))}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined" className={c.filtros}>
        <TextField size="small" variant="outlined" value={busca} onChange={(e) => setBusca(e.target.value)} style={{ flex: "1 1 320px" }}
          placeholder="Buscar por nome, e-mail, cidade, telefone, etapa ou qualquer resposta dos formulários"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <FormControlLabel control={<Checkbox color="primary" checked={concluidos} onChange={(e) => setConcluidos(e.target.checked)} />}
          label="Mostrar concluídos" />
      </Paper>
      {!dados ? <CircularProgress size={24} /> : (
        <div className={c.quadro}>
          {colunas.map((et, i) => {
            const lista = daColuna(et);
            return (
              <div key={et} className={c.coluna}>
                <div className={c.cabecalho}><span>{`${et === "(sem etapa)" ? "" : `${i + 1}. `}${et}`}</span><span>{lista.length}</span></div>
                <div className={c.lista}>
                  {lista.map((x) => {
                    const r = resumo(x);
                    return (
                      <Card key={x.id} variant="outlined" className={`${c.card} ${x.status === "FECHADO" ? c.fechado : x.para_validar > 0 ? c.validar : ""}`}>
                        <CardActionArea onClick={() => abrir(x.id)}>
                          <CardContent style={{ padding: 10 }}>
                            <Typography variant="body2"><b>{r.nome}</b></Typography>
                            {r.extras.length > 0 && <div className={c.fraco}>{r.extras.join(" · ")}</div>}
                            <div className={c.fraco} style={{ marginTop: 4 }}>
                              {`📝 ${x.respondidos}/${x.formularios} formulário(s)`}
                              {x.para_validar > 0 && <b style={{ color: "#7b1fa2" }}>{` · ${x.para_validar} para validar`}</b>}
                            </div>
                            {x.ultimo && <div className={c.fraco} style={{ fontStyle: "italic", marginTop: 4 }}>{`“${x.ultimo.slice(0, 90)}”`}</div>}
                            <div className={c.fraco} style={{ marginTop: 4 }}>
                              {x.status === "FECHADO" ? "concluído" : `atualizado ${haQuanto(x.atualizado_em)}`}
                            </div>
                          </CardContent>
                        </CardActionArea>
                        {x.whatsapp_ticket_id && (
                          <Button size="small" startIcon={<WhatsAppIcon />} style={{ margin: "0 6px 6px" }}
                            onClick={() => history.push(`/tickets/${x.whatsapp_ticket_id}`)}>Conversa</Button>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Drawer anchor="right" open={!!aberto} onClose={() => { setAberto(null); carregar(); }}>
        <div className={c.ficha}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <Typography variant="h6" style={{ flex: 1 }}>
              {aberto ? `${modelo?.nome || "Card"} · ${(aberto.campos || {}).nome || aberto.solicitante || ""}` : ""}
            </Typography>
            <IconButton size="small" onClick={() => { setAberto(null); carregar(); }}><CloseIcon /></IconButton>
          </div>
          {aberto && modelo && <CardFila card={aberto} modelo={modelo} onAtualizado={setAberto} />}
        </div>
      </Drawer>
    </MainContainer>
  );
};

export default CardsDaFila;
